<script lang="ts" setup>
import { onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import { productsService } from '@/services/products.service';
import type { AdminProduct } from '@/services/products.service';
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { ConnectionError } from '@/services/api';
import type { Config } from 'datatables.net';

const router = useRouter();

const orgsBaseUrl = `${import.meta.env.VITE_API_URL}/orgs`;

// State
const products = ref<AdminProduct[]>([]);
const isLoading = ref(false);
const hasError = ref(false);
const errorType = ref<'connection' | 'http' | 'general'>('general');
const searchQuery = ref('');

// Delete modal state
const showDeleteModal = ref(false);
const productToDelete = ref<AdminProduct | null>(null);

// Load orgs for name lookup
const orgsMap = ref<Record<string, string>>({});

// Computed
const filteredProducts = computed(() => {
  return products.value.filter((product) => {
    const matchesSearch =
      !searchQuery.value ||
      product.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      (product.description || '').toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      getOrgName(product.orgId).toLowerCase().includes(searchQuery.value.toLowerCase());

    return matchesSearch;
  });
});

const tableOptions = computed(() => ({
  columns: [
    {
      data: null,
      title: 'Product',
      render: (_: any, __: string, row: any) => {
        const desc = row.description ? `<small class="text-muted d-block">${row.description}</small>` : '';
        return `<strong>${row.name}</strong>${desc}`;
      }
    },
    {
      data: null,
      title: 'Publisher',
      render: (_: any, __: string, row: any) => {
        const orgName = getOrgName(row.orgId);
        return `<a href="#" class="text-decoration-none" data-action="view-org">${orgName} <i class="bi bi-box-arrow-up-right small"></i></a>`;
      }
    },
    {
      data: null,
      title: '',
      orderable: false,
      className: 'text-end',
      render: () => `<button class="bb-btn-danger btn-sm text-nowrap" data-action="delete"><i class="bi bi-trash me-1"></i> Delete</button>`
    }
  ],
  layout: { topEnd: '', bottomEnd: '', bottomStart: '' }
} as Config));

// Methods
async function loadProducts() {
  isLoading.value = true;
  hasError.value = false;
  try {
    products.value = await productsService.listAllProducts();
    // Load org names
    await loadOrgNames();
  } catch (error) {
    console.error('Error loading products:', error);
    hasError.value = true;

    // Determine error type
    if (error instanceof ConnectionError) {
      errorType.value = 'connection';
    } else {
      errorType.value = 'general';
    }
  } finally {
    isLoading.value = false;
  }
}

async function loadOrgNames() {
  try {
    const orgs = await fetchWrapper.get(orgsBaseUrl) as any[];
    if (orgs && Array.isArray(orgs)) {
      orgs.forEach((org: any) => {
        orgsMap.value[org.orgId] = org.orgName || 'Unknown';
      });
    }
  } catch (error) {
    console.error('Error loading orgs:', error);
  }
}

function getOrgName(orgId: string | undefined): string {
  if (!orgId) return 'Unknown';
  return orgsMap.value[orgId] || orgId;
}

function handleRowClick(row: AdminProduct, _: number, event: Event) {
  const target = event.target as HTMLElement;
  if (target.closest('[data-action="delete"]')) {
    productToDelete.value = row;
    showDeleteModal.value = true;
  } else if (target.closest('[data-action="view-org"]')) {
    if (row.orgId) router.push(`/admin/organisations/${row.orgId}`);
  }
}

async function confirmDelete() {
  if (!productToDelete.value) return;
  try {
    await productsService.deleteProduct(productToDelete.value.id);
    products.value = products.value.filter((p) => p.id !== productToDelete.value!.id);
  } catch (err: any) {
    alert(`Failed to delete product: ${err.message}`);
  } finally {
    showDeleteModal.value = false;
    productToDelete.value = null;
  }
}

onMounted(async () => {
  await loadProducts();
});
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Products"
        :breadcrumbs="[{ label: 'Dashboard', href: '/' }, { label: 'Admin', href: '/admin' }, { label: 'Products' }]"
      />

      <!-- Filters -->
      <div class="row g-3 mb-4">
        <div class="col-12">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Search products by name, description, or publisher..."
          />
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState
        v-else-if="hasError"
        :type="errorType"
        @retry="loadProducts"
      />

      <!-- Products Table -->
      <DataView
        v-else
        :data="filteredProducts"
        :view-mode="'table'"
        :options="tableOptions"
        @row-click="handleRowClick"
      >
        <template #empty-state>
          <div class="text-center py-5 text-muted">
            <i class="bi bi-box-seam fs-1 mb-3"></i>
            <p>No products found</p>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>

  <!-- Delete Modal -->
  <div class="modal fade" :class="{ show: showDeleteModal }" :style="{ display: showDeleteModal ? 'block' : 'none' }" @click.self="showDeleteModal = false">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger"><i class="bi bi-exclamation-triangle me-2"></i>Delete Product</h2>
          <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{ productToDelete?.name }}</strong>?</p>
          <p class="text-muted mb-0">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="bb-btn-cancel" @click="showDeleteModal = false">Cancel</button>
          <button class="btn btn-danger" @click="confirmDelete">Delete</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showDeleteModal" class="modal-backdrop fade show" @click="showDeleteModal = false"></div>
</template>
